import * as React from "react"
import { CheckIcon } from "lucide-react"
import { IconFilterCheck } from "@tabler/icons-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { useTranslation } from "react-i18next"

interface DataTableFacetedFilterProps {
  title?: string
  options: {
    label: string
    value: string
  }[]
  onFacetChange?: (values: string[]) => void
  defaultSelected?: string[]
  triggerId?: string
}

export function DataTableFacetedFilter({
  title,
  options,
  onFacetChange,
  defaultSelected = [],
  triggerId
}: DataTableFacetedFilterProps) {
  const i18n = useTranslation()
  const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultSelected)

  const handleSelect = React.useCallback(
    (value: string) => {
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]

      setSelectedValues(newSelectedValues)

      // Call the callback with the new selected values
      if (onFacetChange) {
        onFacetChange(newSelectedValues)
      }
    },
    [selectedValues, onFacetChange],
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed text-sm" data-testid={triggerId}>
          <IconFilterCheck className="me-2 h-4 w-4" />
          {title}
          {selectedValues.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {i18n.t('selected', { selected: selectedValues.length })}
              </Badge>
              <div className="hidden gap-1 lg:flex">
                {selectedValues.length > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1.5 font-normal">
                    {i18n.t('selected', { selected: selectedValues.length })}
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.includes(option.value))
                    .map((option) => (
                      <Badge variant="secondary" key={option.value} className="rounded-sm px-1.5 font-normal">
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>{i18n.t('no_results')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.value)
                return (
                  <CommandItem key={option.value} onSelect={() => handleSelect(option.value)}>
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {selectedValues.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      setSelectedValues([])
                      if (onFacetChange) {
                        onFacetChange([])
                      }
                    }}
                    className="justify-center text-center"
                  >
                    {i18n.t('clear_filters')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
